import { useState } from 'react';
import { useQueryClient } from 'react-query';
import { ContactDirectoriesMutation, ContactDirectoriesQueryKeys } from '@frontend/api-contact-directories';
import { useTranslation } from '@frontend/i18n';
import { usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { SurfaceHeader } from '@frontend/surface-header';
import { theme } from '@frontend/theme';
import { ModalControlModalProps, Tray, useAlert } from '@frontend/design-system';
import { AssignDevicesToDirectoryTable } from '../tables/assign-devices-to-directory-table';

type Props = ModalControlModalProps & {
  name: string;
  contactDirectoryId: string;
};

export const AssignDevicesToDirectoryModal = ({ onClose, name, contactDirectoryId }: Props) => {
  const { t } = useTranslation('phone', { keyPrefix: 'contact-directories' });
  const [selectedDevicesIds, setSelectedDevicesIds] = useState<string[]>();
  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');
  const tenantId = settingsTenantLocation?.phoneTenantId ?? '';
  const alert = useAlert();
  const queryClient = useQueryClient();

  const { useReplaceContactDirectoryDevicesMutation } = ContactDirectoriesMutation.useContactDirectoryMutations();
  const { mutateAsync: mutateAssignDevices } = useReplaceContactDirectoryDevicesMutation({
    onSuccess: () => {
      onClose();
      alert.success(t('Successfully updated devices using this list'));
      queryClient.invalidateQueries([tenantId, ...ContactDirectoriesQueryKeys.queryKeys.getContactDirectories()]);
      queryClient.invalidateQueries([
        tenantId,
        ...ContactDirectoriesQueryKeys.queryKeys.getDevicesbyContactDirectoryId(contactDirectoryId),
      ]);
    },
    onSettled: () => {
      setSelectedDevicesIds(undefined);
    },
  });

  return (
    <>
      <SurfaceHeader.Root noBorder css={{ margin: theme.spacing(-4, -4, 0, -4), width: 'calc(100% + 64px)' }}>
        <SurfaceHeader.Row>
          <SurfaceHeader.Title>{t('Assign or Remove List')}</SurfaceHeader.Title>
          <SurfaceHeader.Actions>
            <SurfaceHeader.Icon iconName='x' onClick={onClose} />
          </SurfaceHeader.Actions>
        </SurfaceHeader.Row>
        <SurfaceHeader.Body>Choose which devices use {name} in their phone directory.</SurfaceHeader.Body>
      </SurfaceHeader.Root>
      <Tray.Body>
        <AssignDevicesToDirectoryTable
          setSelectedDevicesIds={setSelectedDevicesIds}
          contactDirectoryId={contactDirectoryId}
        />
      </Tray.Body>
      <Tray.Actions
        onSecondaryClick={onClose}
        secondaryLabel='Cancel'
        primaryLabel='Save'
        primaryTrackingId='assign-devices-directories'
        onPrimaryClick={() => {
          mutateAssignDevices({ contactListId: contactDirectoryId, deviceIds: selectedDevicesIds ?? [] });
        }}
      />
    </>
  );
};
